import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { IceCream, Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";

export default function GelateriaConfig() {
  const [formats, setFormats] = useState([]);
  const [extras, setExtras] = useState([]);
  const [gusti, setGusti] = useState([]);
  const [gustiCat, setGustiCat] = useState(null);
  const [newGusto, setNewGusto] = useState("");
  const [saving, setSaving] = useState(false);

  const loadGusti = async () => {
    const [rc, rp] = await Promise.all([api.get("/categories?mode=gelateria"), api.get("/products?mode=gelateria")]);
    const cats = Array.isArray(rc.data) ? rc.data : [];
    const cat = cats.find((c) => (c.name || "").trim().toLowerCase() === "gusti");
    setGustiCat(cat || null);
    setGusti(cat ? (Array.isArray(rp.data) ? rp.data : []).filter((p) => p.category_id === cat.id) : []);
  };

  useEffect(() => {
    api.get("/gelateria/config").then((r) => {
      setFormats(Array.isArray(r.data.formats) ? r.data.formats : []);
      setExtras(Array.isArray(r.data.extras) ? r.data.extras : []);
    });
    loadGusti();
  }, []);

  const setFmt = (i, field, value) => setFormats((prev) => (Array.isArray(prev) ? prev : []).map((f, idx) => (idx === i ? { ...f, [field]: value } : f)));
  const setExt = (i, field, value) => setExtras((prev) => (Array.isArray(prev) ? prev : []).map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));

  const saveConfig = async () => {
    if (!formats.some((f) => (f.label || "").trim())) return toast.error("Serve almeno un formato con nome");
    setSaving(true);
    try {
      const r = await api.put("/gelateria/config", { formats, extras });
      setFormats(Array.isArray(r.data.formats) ? r.data.formats : []);
      setExtras(Array.isArray(r.data.extras) ? r.data.extras : []);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const addGusto = async () => {
    const name = newGusto.trim();
    if (!name) return;
    try {
      let cat = gustiCat;
      if (!cat) {
        const rc = await api.post("/categories", { name: "Gusti", color: "#F472B6", mode: "gelateria" });
        cat = rc.data;
        setGustiCat(cat);
      }
      await api.post("/products", { name, category_id: cat.id, price: 0, vat: 10, mode: "gelateria" });
      setNewGusto("");
      toast.success(`Gusto "${name}" aggiunto`);
      loadGusti();
    } catch {
      toast.error("Errore aggiunta gusto");
    }
  };

  const toggleGusto = async (g) => {
    try {
      await api.patch(`/products/${g.id}`, { ...g, visible: g.visible === false });
      loadGusti();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const deleteGusto = async (g) => {
    try {
      await api.delete(`/products/${g.id}`);
      toast.success(`Gusto "${g.name}" eliminato`);
      loadGusti();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const inputCls = "h-10 px-3 bg-white border border-velta-border rounded-md text-sm w-full focus:outline-none focus:border-velta-ink";

  return (
    <div className="space-y-6" data-testid="gelateria-config-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-1">Gestionale</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <IceCream className="w-7 h-7 text-[#EC4899]" /> Configurazione Gelateria
          </h1>
          <p className="text-velta-mute text-sm mt-1">Formati, extra e gusti usati dalla cassa Gelateria.</p>
        </div>
        <button
          data-testid="gelcfg-save-btn"
          onClick={saveConfig}
          disabled={saving}
          className="h-12 px-6 bg-velta-ink text-white rounded-md font-medium flex items-center gap-2 btn-tactile hover:bg-[#162244] disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva formati & extra"}
        </button>
      </div>

      {/* FORMATI */}
      <div className="bg-white border border-velta-border rounded-md p-5" data-testid="gelcfg-formats-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-semibold">Formati</h2>
            <p className="text-velta-mute text-xs">Nome, tipo (icona in cassa), numero di gusti inclusi e prezzo.</p>
          </div>
          <button
            data-testid="gelcfg-add-format-btn"
            onClick={() => setFormats((prev) => [...prev, { label: "", type: "cono", gusti: 1, price: 0 }])}
            className="h-10 px-3 bg-white border border-velta-border rounded-md text-sm font-medium flex items-center gap-1.5 hover:border-velta-ink btn-tactile"
          >
            <Plus className="w-4 h-4" /> Aggiungi formato
          </button>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_140px_110px_110px_40px] gap-2 px-1 pb-1 text-[10px] font-mono uppercase tracking-widest text-velta-mute">
          <span>Nome</span><span>Tipo</span><span>Gusti inclusi</span><span>Prezzo €</span><span></span>
        </div>
        <div className="space-y-2">
          {(Array.isArray(formats) ? formats : []).map((f, i) => (
            <div key={f.id || `new-${i}`} className="grid grid-cols-2 sm:grid-cols-[1fr_140px_110px_110px_40px] gap-2 items-center" data-testid={`gelcfg-format-row-${i}`}>
              <input data-testid={`gelcfg-format-label-${i}`} className={inputCls} placeholder="es. Cono medio" value={f.label} onChange={(e) => setFmt(i, "label", e.target.value)} />
              <select data-testid={`gelcfg-format-type-${i}`} className={inputCls} value={f.type || "altro"} onChange={(e) => setFmt(i, "type", e.target.value)}>
                <option value="cono">🍦 Cono</option>
                <option value="coppetta">🍨 Coppetta</option>
                <option value="altro">🍧 Altro</option>
              </select>
              <input data-testid={`gelcfg-format-gusti-${i}`} className={inputCls} type="number" min="1" step="1" value={f.gusti} onChange={(e) => setFmt(i, "gusti", e.target.value)} />
              <input data-testid={`gelcfg-format-price-${i}`} className={inputCls} type="number" min="0" step="0.10" value={f.price} onChange={(e) => setFmt(i, "price", e.target.value)} />
              <button data-testid={`gelcfg-format-del-${i}`} onClick={() => setFormats((prev) => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== i))} className="h-10 w-10 flex items-center justify-center text-velta-mute hover:text-velta-error rounded-md hover:bg-velta-bg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {formats.length === 0 && <div className="text-center text-velta-mute text-sm py-6">Nessun formato — aggiungine almeno uno.</div>}
        </div>
      </div>

      {/* EXTRA */}
      <div className="bg-white border border-velta-border rounded-md p-5" data-testid="gelcfg-extras-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-semibold">Extra</h2>
            <p className="text-velta-mute text-xs">Aggiunte opzionali (panna, topping…) con prezzo aggiuntivo.</p>
          </div>
          <button
            data-testid="gelcfg-add-extra-btn"
            onClick={() => setExtras((prev) => [...prev, { label: "", price: 0 }])}
            className="h-10 px-3 bg-white border border-velta-border rounded-md text-sm font-medium flex items-center gap-1.5 hover:border-velta-ink btn-tactile"
          >
            <Plus className="w-4 h-4" /> Aggiungi extra
          </button>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_110px_40px] gap-2 px-1 pb-1 text-[10px] font-mono uppercase tracking-widest text-velta-mute max-w-2xl">
          <span>Nome</span><span>Prezzo €</span><span></span>
        </div>
        <div className="space-y-2 max-w-2xl">
          {(Array.isArray(extras) ? extras : []).map((e, i) => (
            <div key={e.id || `new-${i}`} className="grid grid-cols-[1fr_110px_40px] gap-2 items-center" data-testid={`gelcfg-extra-row-${i}`}>
              <input data-testid={`gelcfg-extra-label-${i}`} className={inputCls} placeholder="es. Panna" value={e.label} onChange={(ev) => setExt(i, "label", ev.target.value)} />
              <input data-testid={`gelcfg-extra-price-${i}`} className={inputCls} type="number" min="0" step="0.10" value={e.price} onChange={(ev) => setExt(i, "price", ev.target.value)} />
              <button data-testid={`gelcfg-extra-del-${i}`} onClick={() => setExtras((prev) => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== i))} className="h-10 w-10 flex items-center justify-center text-velta-mute hover:text-velta-error rounded-md hover:bg-velta-bg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {extras.length === 0 && <div className="text-center text-velta-mute text-sm py-6">Nessun extra configurato.</div>}
        </div>
      </div>

      {/* GUSTI */}
      <div className="bg-white border border-velta-border rounded-md p-5" data-testid="gelcfg-gusti-card">
        <div className="mb-4">
          <h2 className="font-heading text-lg font-semibold">Gusti disponibili</h2>
          <p className="text-velta-mute text-xs">
            I gusti sono articoli della categoria &quot;Gusti&quot; (modalità Gelateria) — modificabili anche da Articoli &amp; Categorie. Salvataggio immediato.
          </p>
        </div>
        <div className="flex gap-2 mb-4 max-w-md">
          <input
            data-testid="gelcfg-new-gusto-input"
            className={inputCls}
            placeholder="Nuovo gusto (es. Pistacchio)"
            value={newGusto}
            onChange={(e) => setNewGusto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addGusto()}
          />
          <button data-testid="gelcfg-add-gusto-btn" onClick={addGusto} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium flex items-center gap-1.5 btn-tactile hover:bg-[#162244] shrink-0">
            <Plus className="w-4 h-4" /> Aggiungi
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(Array.isArray(gusti) ? gusti : []).map((g) => (
            <div
              key={g.id}
              data-testid={`gelcfg-gusto-${g.id}`}
              className={`flex items-center gap-1.5 border rounded-full pl-3 pr-1 h-9 text-sm font-medium ${g.visible === false ? "border-velta-border text-velta-mute bg-velta-bg line-through" : "border-[#F472B6] bg-pink-50 text-velta-ink"}`}
            >
              {g.name}
              <button data-testid={`gelcfg-gusto-toggle-${g.id}`} onClick={() => toggleGusto(g)} title={g.visible === false ? "Rendi visibile in cassa" : "Nascondi dalla cassa"} className="h-7 w-7 flex items-center justify-center text-velta-mute hover:text-velta-ink rounded-full hover:bg-white">
                {g.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button data-testid={`gelcfg-gusto-del-${g.id}`} onClick={() => deleteGusto(g)} className="h-7 w-7 flex items-center justify-center text-velta-mute hover:text-velta-error rounded-full hover:bg-white">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {gusti.length === 0 && <div className="text-velta-mute text-sm py-2">Nessun gusto — aggiungi il primo qui sopra.</div>}
        </div>
      </div>

      <div className="text-xs text-velta-mute flex items-center gap-2">
        <span className="font-mono">{formatEUR(0.5)}</span> Esempio formato prezzi. La cassa Gelateria si aggiorna alla prossima apertura.
      </div>
    </div>
  );
}
